/**
 * CoachingCommitmentsTile — home-page surface for maturity-action pins.
 *
 * Filters the user's quarterly-deck pins down to `theme:
 * 'maturity-action'` (i.e. the things they pinned from the
 * Maturity Action Plan card) and renders each as a compact
 * rose-tinted row. The visual distinction matters: these pins
 * are *coaching commitments*, not vanity metrics — leaders
 * scanning their pins should be able to spot at a glance which
 * ones represent a real action they've signed up to take.
 *
 * Empty state: the component renders nothing. We deliberately
 * skip the "no commitments yet" placeholder so the home page
 * stays clean for users who haven't engaged with the action-plan
 * surface — they'll discover it organically from the Maturity
 * Health dashboard.
 *
 * Data: GET /api/quarterly-deck/pins
 * Mutation: DELETE /api/quarterly-deck/pins/{id} ("Mark complete")
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Target, ClipboardCheck, ExternalLink, Loader2, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';

// "X days ago" for the pinned_at chip. We don't pull dayjs/dates-
// fns just for one calculation — a short helper keeps the bundle
// honest.
function formatRelative(iso) {
  if (!iso) return '';
  const ts = typeof iso === 'string' ? new Date(iso) : iso;
  if (isNaN(ts.getTime())) return '';
  const days = Math.floor((Date.now() - ts.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export function CoachingCommitmentsTile() {
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  // Org's commitment-completion cadence (median days + percentile
  // vs other orgs). null until the cadence endpoint resolves; we
  // re-fetch after every successful Done so the footer reflects
  // the new data point without a page refresh.
  const [cadence, setCadence] = useState(null);

  const loadCadence = useCallback(async () => {
    try {
      const res = await api.get('/analytics/weak-link/my-cadence');
      setCadence(res?.data || null);
    } catch {
      // Cadence is a "nice to have" footer — failing silently
      // keeps the tile clean.
      setCadence(null);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/quarterly-deck/pins');
      const all = res?.data?.pins || [];
      setPins(all.filter((p) =>
        p?.theme === 'maturity-action'
        || p?.theme === 'lei-action'
        || p?.theme === 'pe-action'
        || p?.theme === 'roe-action'
        || p?.theme === 'roi-action'
        || p?.theme === 'quarterly-fix-plan',
      ));
    } catch (err) {
      logger.error?.('coaching commitments load failed', err);
      setPins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCadence(); }, [loadCadence]);

  const handleComplete = async (pin) => {
    if (completingId) return;
    setCompletingId(pin.id);
    // Capture the celebratory event *before* the delete so a
    // successful celebration is recorded even if the DELETE fails
    // (the user clearly intended to mark this done; analytics
    // shouldn't punish them for a 5xx). Wins this week digest
    // reads from the same `weak_link_events` collection.
    const titleNoPrefix = (pin.title || '').replace(/^(Maturity|LEI|PE|ROE|ROI) Action:\s*|^Q-Fix:\s*/i, '');
    api.post('/analytics/weak-link/commitment-completed', {
      pillar_id: pin.theme || null,
      pillar_name: titleNoPrefix || null,
      pin_id: pin.id,
      pin_title: pin.title || null,
      pinned_at: pin.pinned_at || null,
      source: 'home_dashboard',
    }).catch(() => {});
    try {
      await api.delete(`/quarterly-deck/pins/${pin.id}`);
      // Optimistic remove — no need to round-trip the list.
      setPins((prev) => prev.filter((p) => p.id !== pin.id));
      toast.success('Commitment closed — nice work.');
      // Refresh the cadence so the footer reflects the new data
      // point. Fire-and-forget; toast already gave the user
      // confirmation that the action succeeded.
      loadCadence();
    } catch (err) {
      logger.error?.('coaching commitment complete failed', err);
      toast.error('Could not close commitment — try again.');
    } finally {
      setCompletingId(null);
    }
  };

  // Hide the entire tile during the initial load + the empty
  // state so the home page doesn't churn for users who never
  // engage with the action-plan surface.
  if (loading) return null;
  if (pins.length === 0) return null;

  return (
    <Card
      id="coaching-commitments"
      className="border-rose-200 bg-gradient-to-br from-rose-50 to-white scroll-mt-24"
      data-testid="coaching-commitments-tile"
    >
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-rose-100">
              <Target className="h-4 w-4 text-rose-600" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-rose-700">
                Coaching commitments
              </p>
              <p className="text-sm font-bold text-slate-900">
                {pins.length} action{pins.length === 1 ? '' : 's'} you've pinned to your Quarterly Deck
              </p>
            </div>
          </div>
        </div>
        <ul className="space-y-1.5">
          {pins.map((pin) => {
            // Pull the "first move" stat back out so the row reads
            // "Strategic & Governance · Charter a learning council".
            const firstMoveStat = (pin.stats || []).find((s) => s?.label === 'First move');
            const scoreStat = (pin.stats || []).find((s) => s?.label === 'Pillar score');
            const subtitle = pin.title?.replace(/^(Maturity|LEI|PE|ROE|ROI) Action:\s*|^Q-Fix:\s*/i, '') || 'Coaching action';
            return (
              <li
                key={pin.id}
                className="flex items-center gap-3 px-3 py-2 rounded-md bg-white border border-rose-100 hover:border-rose-300 transition-colors"
                data-testid={`coaching-commitment-row-${pin.id}`}
              >
                <div className="shrink-0 p-1 rounded-md bg-rose-50">
                  <ClipboardCheck className="h-3.5 w-3.5 text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {subtitle}
                    {scoreStat?.value && (
                      <span className="ml-1.5 text-[11px] font-medium text-rose-700">
                        {scoreStat.value}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {firstMoveStat?.value
                      ? <>Next: <strong className="text-slate-700">{firstMoveStat.value}</strong></>
                      : 'Open the dashboard to see the playbook.'}
                    <span className="text-slate-400"> · pinned {formatRelative(pin.pinned_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[11px] text-slate-500 hover:text-slate-900 px-2 h-7"
                    onClick={() => navigate(pin.path || '/maturity-health')}
                    data-testid={`coaching-commitment-open-${pin.id}`}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Open
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[11px] text-emerald-700 hover:bg-emerald-50 px-2 h-7"
                    onClick={() => handleComplete(pin)}
                    disabled={completingId === pin.id}
                    data-testid={`coaching-commitment-complete-${pin.id}`}
                  >
                    {completingId === pin.id ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    Done
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Cadence footer — only renders when we have at least
            one closed commitment in the window. Speaks the org's
            own median first ("your team's median: 11 days") and
            adds a peer percentile when there's enough peer data
            to make the comparison meaningful (≥2 peer orgs). */}
        {cadence?.my_median_days != null && (
          <div
            className="mt-3 flex items-center justify-between text-[11px] text-slate-500 px-1"
            data-testid="coaching-commitments-cadence-footer"
          >
            <span>
              Your team's median time-to-close:
              {' '}
              <strong className="text-slate-800">
                {cadence.my_median_days < 1
                  ? '<1 day'
                  : cadence.my_median_days < 14
                    ? `${Math.round(cadence.my_median_days * 10) / 10} day${cadence.my_median_days >= 2 ? 's' : ''}`
                    : `${Math.round(cadence.my_median_days / 7 * 10) / 10} weeks`}
              </strong>
            </span>
            {cadence.percentile_vs_peers != null && (
              <span
                className={
                  cadence.percentile_vs_peers >= 50
                    ? 'text-emerald-700 font-semibold'
                    : 'text-rose-700 font-semibold'
                }
              >
                {cadence.percentile_vs_peers >= 50 ? '·' : '·'}
                {' '}faster than {cadence.percentile_vs_peers}% of peers
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CoachingCommitmentsTile;
